import React, { useState } from 'react';
import { useDesignStore } from '../../stores/designStore';
import type { DesignSuggestion, DesignSuggestionInput } from '../../types';

interface SuggestionCardProps {
  suggestion: DesignSuggestion;
  onApply: () => void;
  onIgnore: () => void;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({ suggestion, onApply, onIgnore }) => {
  const [isApplying, setIsApplying] = useState(false);

  const getTypeIcon = (type: DesignSuggestion['type']) => {
    switch (type) {
      case 'color':
        return (
          <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </div>
        );
      case 'layout':
        return (
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          </div>
        );
      case 'typography':
        return (
          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          </div>
        );
      case 'spacing':
        return (
          <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
        );
      case 'content':
        return (
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getTypeLabel = (type: DesignSuggestion['type']) => {
    const labels: Record<DesignSuggestion['type'], string> = {
      color: '颜色',
      layout: '布局',
      typography: '字体',
      spacing: '间距',
      content: '内容',
      other: '其他',
    };
    return labels[type];
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      // TODO: 实际应用建议的逻辑
      await new Promise(resolve => setTimeout(resolve, 500));
      onApply();
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="p-3 bg-white border-b last:border-b-0">
      <div className="flex gap-3">
        {getTypeIcon(suggestion.type)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
              {getTypeLabel(suggestion.type)}
            </span>
          </div>
          <h4 className="text-sm font-medium text-gray-800 mb-1">{suggestion.title}</h4>
          <p className="text-xs text-gray-500 line-clamp-2">{suggestion.description}</p>
          
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleApply}
              disabled={isApplying}
              className="text-xs px-3 py-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 transition-colors"
            >
              {isApplying ? (
                <span className="flex items-center gap-1">
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  应用
                </span>
              ) : (
                '应用'
              )}
            </button>
            <button
              onClick={onIgnore}
              className="text-xs px-3 py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            >
              忽略
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SuggestionsPanel: React.FC = () => {
  const {
    suggestions,
    applySuggestion,
    ignoreSuggestion,
    clearSuggestions,
    currentDesign,
  } = useDesignStore();

  const [activeFilter, setActiveFilter] = useState<DesignSuggestion['type'] | 'all'>('all');
  const [showGenerateMock, setShowGenerateMock] = useState(false);

  // 过滤建议
  const filteredSuggestions = suggestions.filter(
    s => s.status === 'pending' && (activeFilter === 'all' || s.type === activeFilter)
  );

  // 生成模拟建议（用于演示）
  const generateMockSuggestions = () => {
    const mockSuggestions: DesignSuggestionInput[] = [
      {
        type: 'color',
        title: '优化主色调对比度',
        description: '当前主色调与背景对比度较低，建议调整为更深的紫色以提高可读性。',
      },
      {
        type: 'layout',
        title: '调整卡片间距',
        description: '卡片之间的间距可以稍微增大，让页面呼吸感更强。',
      },
      {
        type: 'typography',
        title: '增大标题字号',
        description: '标题字号可以增大到2.5rem，让视觉层次更加分明。',
      },
      {
        type: 'spacing',
        title: '增加段落间距',
        description: '段落之间的间距可以增加8px，提升阅读体验。',
      },
    ];

    mockSuggestions.forEach(suggestion => {
      useDesignStore.getState().addSuggestion(suggestion);
    });
    setShowGenerateMock(false);
  };

  // 处理应用建议
  const handleApply = (suggestionId: string) => {
    applySuggestion(suggestionId);
  };

  // 处理忽略建议
  const handleIgnore = (suggestionId: string) => {
    ignoreSuggestion(suggestionId);
  };

  const filters: { type: DesignSuggestion['type'] | 'all'; label: string }[] = [
    { type: 'all', label: '全部' },
    { type: 'color', label: '颜色' },
    { type: 'layout', label: '布局' },
    { type: 'typography', label: '字体' },
    { type: 'spacing', label: '间距' },
  ];

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 头部 */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="text-xs font-medium text-gray-700">优化建议</span>
          {filteredSuggestions.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full">
              {filteredSuggestions.length}
            </span>
          )}
        </div>
        {filteredSuggestions.length > 0 && (
          <button
            onClick={clearSuggestions}
            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
          >
            清空
          </button>
        )}
      </div>

      {/* 过滤器 */}
      {filteredSuggestions.length > 0 && (
        <div className="px-3 py-2 border-b overflow-x-auto">
          <div className="flex gap-1">
            {filters.map(filter => (
              <button
                key={filter.type}
                onClick={() => setActiveFilter(filter.type)}
                className={`
                  text-xs px-2 py-1 rounded-full whitespace-nowrap transition-colors
                  ${activeFilter === filter.type
                    ? 'bg-purple-100 text-purple-600'
                    : 'text-gray-500 hover:bg-gray-100'
                  }
                `}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 建议列表 */}
      <div className="flex-1 overflow-y-auto">
        {filteredSuggestions.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 p-4">
            <svg className="w-10 h-10 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <p className="text-xs text-center">暂无优化建议</p>
            <p className="text-xs text-center text-gray-300 mt-1">
              AI将自动分析设计并提供建议
            </p>
            
            {/* 开发测试按钮 */}
            {currentDesign && (
              <button
                onClick={() => setShowGenerateMock(true)}
                className="mt-3 text-xs text-purple-500 hover:text-purple-600 underline"
              >
                生成测试建议
              </button>
            )}

            {showGenerateMock && (
              <div className="mt-3 p-2 bg-purple-50 rounded text-xs">
                <p className="text-purple-600 mb-2">生成模拟建议用于测试？</p>
                <div className="flex gap-2">
                  <button
                    onClick={generateMockSuggestions}
                    className="px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
                  >
                    生成
                  </button>
                  <button
                    onClick={() => setShowGenerateMock(false)}
                    className="px-2 py-1 bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                  >
                    取消
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            {filteredSuggestions.map(suggestion => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onApply={() => handleApply(suggestion.id)}
                onIgnore={() => handleIgnore(suggestion.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 底部统计 */}
      {filteredSuggestions.length > 0 && (
        <div className="px-3 py-2 border-t bg-gray-50 text-xs text-gray-400 text-center">
          共 {filteredSuggestions.length} 条建议
        </div>
      )}
    </div>
  );
};
